// Routine definitions + helpers for the 60-day skin tracker

export const TOTAL_DAYS = 60;

export const MORNING_ITEMS = [
  { id: "face_wash", label: "Face Wash", desc: "Gentle cleanser" },
  { id: "clarifying_serum", label: "Clarifying Serum", desc: "Pat into skin" },
  { id: "moisturizer_gel", label: "Moisel-FM Gel", desc: "Lightweight moisturizer" },
  { id: "sunscreen", label: "Sunscreen", desc: "Critical on Isotane" },
  { id: "supplement", label: "Abs Glut-DM", desc: "After breakfast" },
];

export const NIGHT_ITEMS = [
  { id: "face_wash", label: "Face Wash", desc: "Remove the day" },
  { id: "acneout", label: "Acneout Cream", desc: "Thin layer, full face" },
  { id: "pimple_gel", label: "Pimple Gel SOS", desc: "Only on active spots" },
  { id: "under_eye", label: "Under-Eye Cream", desc: "Tiny dab, pat in" },
  { id: "isotane", label: "Isotane Tablet", desc: "After dinner, with water" },
];

export const SYMPTOMS = [
  { id: "dry_lips", label: "Dry lips" },
  { id: "dry_skin", label: "Dry skin" },
  { id: "dry_eyes", label: "Dry eyes" },
  { id: "nosebleed", label: "Nosebleed" },
  { id: "sun_sens", label: "Sun sensitivity" },
  { id: "headache", label: "Headache" },
  { id: "joint_pain", label: "Joint pain" },
  { id: "mood_low", label: "Low mood" },
];

export function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseISO(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function dayNumber(startDate, today = new Date()) {
  const start = parseISO(startDate);
  const ms = today.setHours(0, 0, 0, 0) - start.setHours(0, 0, 0, 0);
  return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
}

export function isotaneDoseForDay(n) {
  // Odd days = 10mg, Even days = 5mg (alternating)
  if (n < 1) return null;
  return n % 2 === 1 ? "10 mg" : "5 mg";
}

export function entryCompletion(entry, morningItems, nightItems) {
  if (!entry) return 0;
  const morning = (morningItems || MORNING_ITEMS).filter((i) => entry.morning?.[i.id]).length;
  const night = (nightItems || NIGHT_ITEMS).filter((i) => entry.night?.[i.id]).length;
  const total = (morningItems || MORNING_ITEMS).length + (nightItems || NIGHT_ITEMS).length;
  if (total === 0) return 0;
  return (morning + night) / total;
}

export function computeStreak(entries, today, startDate, morningItems, nightItems) {
  // count consecutive days backwards from today (or yesterday if today incomplete)
  // where completion >= 0.7
  const byDate = {};
  entries.forEach((e) => (byDate[e.date] = e));
  let streak = 0;
  const cur = parseISO(isoDate(today));
  const start = parseISO(startDate);
  while (cur >= start) {
    const key = isoDate(cur);
    const c = entryCompletion(byDate[key], morningItems, nightItems);
    if (c >= 0.7) {
      streak += 1;
      cur.setDate(cur.getDate() - 1);
    } else {
      break;
    }
  }
  return streak;
}

export function weekNumberForDay(dayNum) {
  if (dayNum < 1) return 0;
  return Math.min(9, Math.ceil(dayNum / 7));
}

// Selfie checkpoints every 5 days → 12 checkpoints across 60 days
export const SELFIE_INTERVAL = 5;
export const SELFIE_COUNT = 12;

export function selfieCheckpointForDay(dayNum) {
  if (dayNum < 1) return 0;
  return Math.min(SELFIE_COUNT, Math.ceil(dayNum / SELFIE_INTERVAL));
}

export function isSelfieDay(dayNum) {
  return dayNum >= 1 && dayNum <= 60 && dayNum % SELFIE_INTERVAL === 0;
}

export function selfieDayLabel(checkpoint) {
  if (checkpoint === 0) return 1;
  return checkpoint * SELFIE_INTERVAL;
}

export const DAILY_TIPS = [
  { emoji: "💧", tip: "Drink a glass of water before reaching for your phone each morning." },
  { emoji: "🌿", tip: "Consistency beats intensity. Small daily actions compound into big results." },
  { emoji: "📚", tip: "Read 10 pages a day. That's 12 books a year without trying hard." },
  { emoji: "🧘", tip: "Two minutes of deep breathing before bed lowers cortisol and improves sleep." },
  { emoji: "☀️", tip: "Sunscreen is the most effective anti-aging product that exists. Wear it daily." },
  { emoji: "🔋", tip: "Protect your energy like money. Say no to things that drain without giving back." },
  { emoji: "🎯", tip: "Write down your three most important tasks the night before. Wake up with purpose." },
  { emoji: "🌙", tip: "Sleep is when your skin regenerates. 7–8 hours isn't a luxury — it's maintenance." },
  { emoji: "💡", tip: "Teach what you learn. Explaining a concept cements it in your own memory." },
  { emoji: "🏃", tip: "A 20-minute walk beats a 5-minute doom scroll for mood and focus." },
  { emoji: "🪴", tip: "Growth is uncomfortable. If you're not slightly out of your depth, you're not growing." },
  { emoji: "📝", tip: "Journaling for five minutes a day clears mental fog better than most productivity apps." },
  { emoji: "🤝", tip: "The best investment you can make is in relationships. Reply to that message you've been delaying." },
  { emoji: "⏰", tip: "Do the hardest task first. Everything after that feels easy." },
  { emoji: "🔍", tip: "Curiosity is a skill. Ask 'why' one more time than feels comfortable." },
  { emoji: "🧠", tip: "Your brain solidifies memories during sleep. Review notes before bed, not in the morning." },
  { emoji: "🌱", tip: "Progress photos exist for a reason — your eyes adjust to gradual change. Trust the data." },
  { emoji: "🎵", tip: "Instrumental music while working boosts focus without hijacking the language centre of your brain." },
  { emoji: "💬", tip: "Listen to understand, not to reply. Most conflicts dissolve when people feel heard." },
  { emoji: "🥗", tip: "What you eat shows up on your skin. Omega-3s, zinc, and hydration are free skincare." },
  { emoji: "📵", tip: "First 30 minutes after waking — no phone. Let your brain wake on its own terms." },
  { emoji: "🔄", tip: "Habits are easier to maintain than to restart. Miss one day, never two." },
  { emoji: "🗺️", tip: "Plans change but direction matters. Know roughly where you're headed even if the path shifts." },
  { emoji: "🛁", tip: "Hot showers strip your skin barrier. End with 30 seconds of cold water — your skin will thank you." },
  { emoji: "✍️", tip: "Writing by hand engages your brain differently. Handwrite your goals once a week." },
  { emoji: "🌊", tip: "Stress spikes inflammation, which worsens skin. Managing your mind is part of your skincare routine." },
  { emoji: "🎓", tip: "Spend 30 minutes a day learning something outside your field. It makes you better in your field." },
  { emoji: "🌅", tip: "Morning light (even cloudy) regulates your circadian rhythm and improves sleep quality at night." },
  { emoji: "🏋️", tip: "Exercise increases blood flow to skin cells. Your workout is also a facial." },
  { emoji: "🧩", tip: "Break big goals into two-week sprints. Sixty days is just four of those." },
  { emoji: "🔑", tip: "Moisturise within 60 seconds of washing your face — that's when your skin absorbs most." },
  { emoji: "📖", tip: "Read biographies. Seeing how others solved hard problems expands your solution space." },
  { emoji: "🧴", tip: "Less is more with skincare. Stacking too many actives causes more problems than it solves." },
  { emoji: "🌟", tip: "Celebrate small wins loudly. Your brain needs reinforcement to build new habits." },
  { emoji: "🏖️", tip: "Recovery is part of the work. Rest days, rest weeks — schedule them intentionally." },
  { emoji: "🪞", tip: "Be kind to the person in the mirror. Self-criticism is the slowest path to improvement." },
  { emoji: "⚡", tip: "Energy follows attention. Notice what you focus on and you'll understand how you feel." },
  { emoji: "🐢", tip: "Slow is smooth. Smooth is fast. Rushing often creates the most rework." },
  { emoji: "🌍", tip: "Go outside without headphones sometimes. Boredom is where ideas live." },
  { emoji: "🧪", tip: "Introduce new skincare products one at a time so you know exactly what's working." },
  { emoji: "💎", tip: "Finishing things is a rare skill. A completed imperfect project beats a perfect abandoned one." },
  { emoji: "🤸", tip: "Stretching for 10 minutes before bed improves circulation and lowers cortisol." },
  { emoji: "🦷", tip: "Gut health and skin health are deeply linked. Probiotics and fibre are underrated skincare." },
  { emoji: "🔭", tip: "Zoom out regularly. Most things that feel urgent today won't matter in a year." },
  { emoji: "🎨", tip: "Make something with your hands once a week — it rewires stress pathways in the brain." },
  { emoji: "💌", tip: "Tell people you appreciate them. Nobody hears it enough." },
  { emoji: "🌿", tip: "Your skin is a reflection of your overall health. The outside and inside are one system." },
  { emoji: "🔮", tip: "Visualise how you want to feel at day 60, not just how you want to look." },
  { emoji: "🏔️", tip: "Hard things get easier not because they change, but because you change." },
  { emoji: "🌻", tip: "Find one thing per day that went well and write it down. It rewires your brain toward optimism." },
  { emoji: "💆", tip: "Facial massage for two minutes improves lymphatic drainage and reduces puffiness — for free." },
  { emoji: "📡", tip: "You absorb the energy of content you consume. Audit your feeds like you audit your diet." },
  { emoji: "🎯", tip: "Done is better than perfect, but a little better every day beats both." },
  { emoji: "🧵", tip: "Patch test new skincare for 48 hours. Two days of patience prevents weeks of recovery." },
  { emoji: "🌙", tip: "Night is when your skin barrier repairs itself. A good night cream is an overnight investment." },
  { emoji: "🧭", tip: "Values are a compass, not a destination. Make decisions based on who you want to be." },
  { emoji: "🌈", tip: "Eat a variety of coloured vegetables. Each colour signals different antioxidants for skin and health." },
  { emoji: "🏡", tip: "Your environment shapes your behaviour more than willpower does. Design your space for the habits you want." },
  { emoji: "🦋", tip: "Change is slow until it's sudden. Keep going even when nothing seems to be happening." },
  { emoji: "🎤", tip: "Talk to yourself the way you'd talk to someone you love. Language shapes your internal reality." },
];

export function getDailyTip(dayNum) {
  const idx = ((dayNum - 1) % DAILY_TIPS.length + DAILY_TIPS.length) % DAILY_TIPS.length;
  return DAILY_TIPS[idx];
}

// Plant growth stage 0..6 based on streak + total completed days
export function plantStage(streak, completedDays) {
  // Max possible score with a perfect 60-day run: 60×1.5 + 60×0.4 = 114
  const score = streak * 1.5 + completedDays * 0.4;
  if (score >= 105) return 10;
  if (score >= 91)  return 9;
  if (score >= 78)  return 8;
  if (score >= 65)  return 7;
  if (score >= 52)  return 6;
  if (score >= 38)  return 5;
  if (score >= 25)  return 4;
  if (score >= 15)  return 3;
  if (score >= 8)   return 2;
  if (score >= 3)   return 1;
  return 0;
}

export const STAGE_NAMES = [
  "Seed",
  "Sprout",
  "Seedling",
  "Young Plant",
  "Budding",
  "Flourishing",
  "Blooming",
  "Vibrant",
  "Radiant",
  "Luminous",
  "In Full Bloom",
];

export const BADGES = [
  { id: "first_petal", label: "First Petal", desc: "Complete day 1", emoji: "🌱" },
  { id: "rooted", label: "Rooted", desc: "Hit a 7-day streak", emoji: "🌿" },
  { id: "sprouted", label: "Sprouted", desc: "14-day streak", emoji: "🪴" },
  { id: "blossom", label: "Blossom", desc: "30-day streak", emoji: "🌸" },
  { id: "full_bloom", label: "Full Bloom", desc: "60 days done", emoji: "🌺" },
  { id: "halfway", label: "Halfway Hero", desc: "Reach day 30", emoji: "⛰️" },
  { id: "early_bird", label: "Early Bird", desc: "14 mornings done", emoji: "🌅" },
  { id: "night_owl", label: "Night Owl", desc: "14 nights done", emoji: "🌙" },
  { id: "perfectionist", label: "Perfectionist", desc: "7 days 100% complete", emoji: "✨" },
  { id: "hydrated", label: "Hydrated", desc: "Hit water goal once", emoji: "💧" },
  { id: "wave_rider", label: "Wave Rider", desc: "Water goal 7 days", emoji: "🌊" },
  { id: "self_portrait", label: "Self-Portrait", desc: "First selfie taken", emoji: "📸" },
  { id: "time_traveler", label: "Time Traveler", desc: "6 selfies captured", emoji: "🪞" },
  { id: "storyteller", label: "Storyteller", desc: "All 12 selfies", emoji: "🎞️" },
  { id: "clear_skies", label: "Clear Skies", desc: "10 symptom-free days", emoji: "☀️" },
  { id: "comeback", label: "Comeback Kid", desc: "Resume after a missed day", emoji: "🔁" },
];

export function computeBadgeStatus({ entries, photos, streak, dayNum, waterGoal, morningItems, nightItems }) {
  const status = {};
  const fullDays = entries.filter((e) => entryCompletion(e, morningItems, nightItems) === 1).length;
  const morningsDone = entries.filter((e) =>
    (morningItems || MORNING_ITEMS).every((i) => e.morning?.[i.id])
  ).length;
  const nightsDone = entries.filter((e) =>
    (nightItems || NIGHT_ITEMS).every((i) => e.night?.[i.id])
  ).length;
  const waterDays = entries.filter((e) => (e.water || 0) >= (waterGoal || 8)).length;
  const symptomFreeDays = entries.filter(
    (e) => entryCompletion(e, morningItems, nightItems) > 0 && (!e.symptoms || e.symptoms.length === 0)
  ).length;
  const photosCount = (photos || []).length;

  // Comeback: any past entry missed (<0.7) followed by a later entry >= 0.7
  let comeback = false;
  const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  for (let i = 1; i < sorted.length; i++) {
    if (
      entryCompletion(sorted[i - 1], morningItems, nightItems) < 0.7 &&
      entryCompletion(sorted[i], morningItems, nightItems) >= 0.7
    ) {
      comeback = true;
      break;
    }
  }

  status.first_petal = streak >= 1;
  status.rooted = streak >= 7;
  status.sprouted = streak >= 14;
  status.blossom = streak >= 30;
  status.full_bloom = dayNum >= 60 && fullDays >= 1;
  status.halfway = dayNum >= 30;
  status.early_bird = morningsDone >= 14;
  status.night_owl = nightsDone >= 14;
  status.perfectionist = fullDays >= 7;
  status.hydrated = waterDays >= 1;
  status.wave_rider = waterDays >= 7;
  status.self_portrait = photosCount >= 1;
  status.time_traveler = photosCount >= 6;
  status.storyteller = photosCount >= 12;
  status.clear_skies = symptomFreeDays >= 10;
  status.comeback = comeback;
  return status;
}
